"use client";

import {
  ComponentProps,
  ReactNode,
  type MouseEvent,
  useEffect,
  useState,
} from "react";
import { LegacyButton } from "@zapier/design-system";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import { useLocalStorage } from "src/hooks/useLocalStorage";
import { LOCALSTORAGE_TEMPLATE_CREATE_KEY } from "src/constants/constants";
import { GoogleLogoSvg } from "./GoogleLogoSvg";
import styles from "./GoogleSSOButton.module.css";

const createGoogleSSOUrl = ({
  baseUrl,
  search,
  pathname,
}: {
  baseUrl: string;
  pathname: string;
  search: string;
}) => {
  const parsedUrl = new URL("/google-sso/start", baseUrl);
  parsedUrl.searchParams.set("scopes", "basic");

  let nextParam = pathname;

  if (search) {
    nextParam += `${search}&create=true`;
  } else {
    nextParam += "?create=true";
  }

  parsedUrl.searchParams.set("next", nextParam);

  return parsedUrl.toString();
};

type GoogleSSOButtonProps = {
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
  slug: string;
  size?: "small" | "medium";
};

export const GoogleSSOButton = ({
  onClick,
  children,
  slug,
  size = "medium",
  ...rest
}: GoogleSSOButtonProps & ComponentProps<typeof LegacyButton>) => {
  const { setValue: setLocalStorageTemplateCreateKey } =
    useLocalStorage<string>(LOCALSTORAGE_TEMPLATE_CREATE_KEY);

  const [ssoStartUrl, setSsoStartUrl] = useState<string>();

  useEffect(() => {
    const url = createGoogleSSOUrl({
      baseUrl: window.location.origin,
      pathname: `/templates/details/${slug}`,
      search: window.location.search,
    });
    setSsoStartUrl(url);
  }, [slug]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    emitUserInteractionEvent(UserInteractionEventName.googleSSOButtonClicked, {
      event_action: "click",
      option_selected: "",
    });

    setLocalStorageTemplateCreateKey(slug);

    if (onClick) {
      onClick(event);
    }

    if (ssoStartUrl) {
      window.location.assign(ssoStartUrl);
    }
  };

  return (
    <div className={styles.buttonWrapper}>
      <LegacyButton
        size={size}
        color="tertiary"
        href={ssoStartUrl}
        onClick={handleClick}
        {...rest}
      >
        <span className={styles.buttonContent}>
          <GoogleLogoSvg />
          {children}
        </span>
      </LegacyButton>
    </div>
  );
};
